import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  FlatList,
  Pressable,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import { notifySuccess } from "@components/ds/notify";
import { showApiError } from "@components/ds/showApiError";
import { t } from "@constants/localization";
import {
  getDsRoutingTps,
  assignDsRoutingTps,
  unassignDsRoutingTps,
  markDsRoutingTpOfd,
} from "@services/api/dsRouting";
import { withDateHeaders } from "@screens/Receiving/helpers";
import RoutingHeader from "../components/RoutingHeader";
import RoutingSearch from "../components/RoutingSearch";
import { useRoutingSearch } from "../useRoutingSearch";
import RoutingTpRow from "../components/RoutingTpRow";
import AllOrdersRow from "../components/AllOrdersRow";
import DbPickerSheet from "../components/DbPickerSheet";
import ConfirmPopup from "../components/ConfirmPopup";
import OptionSheet from "../components/OptionSheet";
import {
  FILTERS,
  SCOPES,
  eddLabel,
  filterRoutingTps,
  filterTps,
  getRoutingCounts,
  getCardActions,
  getHandoffConflict,
  getRerackConflict,
  getTpKey,
  routingErrorKey,
} from "../helpers";
import { C, RText, Tag, Pin, ChevronDown, tpName } from "../theme";
import { useRerackGate } from "../useRerackGate";

const PATH = "DsRouting";

// Two modals can't open in the same tick on iOS: let one close first.
const SHEET_SWAP_MS = 300;

// HANDOFF_UNASSIGN: the boxes are with a DB; Remove goes out with
// confirm_handoff. (Change asks its hand-off question in the DB sheet.)
const HANDOFF = "HANDOFF";

const CONFIRM = {
  UNASSIGN: "UNASSIGN",
  OFD: "OFD",
  HANDOFF_UNASSIGN: "HANDOFF_UNASSIGN",
};

const EMPTY_LIST = {
  date: null,
  progress: { to_route: 0, routed: 0, total: 0 },
  localities: [],
  tps: [],
};

const errorKey = routingErrorKey;

// Confirm popup copy per type: i18n title key and confirm-button key.
const CONFIRM_COPY = {
  [CONFIRM.UNASSIGN]: {
    title: `${PATH}/confirmUnassign`,
    label: `${PATH}/actions/remove`,
  },
  [CONFIRM.OFD]: {
    title: `${PATH}/confirmOfd`,
    label: `${PATH}/actions/markOfd`,
  },
  [CONFIRM.HANDOFF_UNASSIGN]: {
    title: `${PATH}/handoff/unassign`,
    label: `${PATH}/actions/remove`,
  },
};

// Which header dropdown's sheet is open.
const SHEET = { LOCALITY: "LOCALITY", FILTER: "FILTER" };

// The "All localities" option (locality state null).
const ALL_LOCALITIES = "__ALL__";

// Filter option → its count field (ALL: total) and empty-state key.
const FILTER_PILLS = [
  { key: FILTERS.ALL, label: "filterAll", count: null, empty: "emptyState" },
  {
    key: FILTERS.PENDING_ASSIGN,
    label: "filterPendingAssign",
    count: "pending_assign",
    empty: "emptyPendingAssign",
  },
  {
    key: FILTERS.PENDING_OFD,
    label: "filterPendingOfd",
    count: "pending_ofd",
    empty: "emptyPendingOfd",
  },
];

// Registered as `DsRoutingTpList`; opened from a DsRoutingHub card with
// `scope`: D0 (EDD D-2..today, the workspace), NEXT (EDD after today,
// read-only, by date) or ALL ("All orders": every TP's status and DB, §10
// frame 07). Pixel-matched to the mockup (§10 frame 08): locality chips,
// the ROUTING PROGRESS box with a bar, then one card per TP. The list is
// fetched once per focus; locality chips and the filter pills (one at a
// time) filter it on the device, counts come from the server.
const TITLE_KEY = {
  [SCOPES.D0]: "d0Title",
  [SCOPES.NEXT]: "nextTitle",
  [SCOPES.ALL]: "allTitle",
};

const DsRoutingTpList = ({ navigation, route }) => {
  const scope = TITLE_KEY[route?.params?.scope]
    ? route.params.scope
    : SCOPES.D0;
  const isNext = scope === SCOPES.NEXT;
  const isAll = scope === SCOPES.ALL;
  const [locality, setLocality] = useState(null);
  const [filter, setFilter] = useState(FILTERS.ALL);
  const [sheet, setSheet] = useState(null);
  const [list, setList] = useState(EMPTY_LIST);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pickerTp, setPickerTp] = useState(null);
  const [confirm, setConfirm] = useState(null);
  // Set when the server answers TP_PICKED_BY_DB to an assign: the DB sheet's
  // own confirm switches to the hand-off question.
  const [handoffFrom, setHandoffFrom] = useState(null);
  // Customer search: filters the loaded list on the device, after the
  // locality and filter pills.
  const search = useRoutingSearch();
  // Moving a picked TP whose DB still holds boxes: re-rack them, then retry.
  const { openRerack, rerackSheet } = useRerackGate();

  const reload = useCallback(
    async ({ refreshing = false } = {}) => {
      try {
        if (refreshing) setIsRefreshing(true);
        else setIsLoading(true);
        // No locality: chips filter on the device, no refetch per tap.
        const response = await getDsRoutingTps({ scope });
        setList(response?.data?.data || EMPTY_LIST);
      } catch (error) {
        showApiError(error, errorKey);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [scope],
  );

  // Reload on focus (e.g. back from Recommended route).
  useFocusEffect(
    useCallback(() => {
      reload();
    }, [reload]),
  );

  // A request may resolve `HANDOFF` (no toast): the server wants the
  // hand-off confirm first.
  const run = async (request, successMessage) => {
    try {
      setSubmitting(true);
      if ((await request()) === HANDOFF) return false;
      notifySuccess(successMessage);
      return true;
    } catch (error) {
      showApiError(error, errorKey);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // One assign. A 400 the screen handles comes back as `handoff` (server
  // without the re-rack gate: ask the hand-off confirm) or `rerack` (the DB
  // still holds picked boxes: re-rack them first).
  const assignTp = async (tp, db, confirmHandoff) => {
    let handoff = null;
    let rerack = null;
    const done = await run(
      async () => {
        const response = await assignDsRoutingTps({
          cx_ids: [tp.cx_id],
          cp_db_id: db.cp_db_id,
          confirm_handoff: confirmHandoff,
        });
        rerack = getRerackConflict(response);
        handoff = rerack ? null : getHandoffConflict(response);
        if (rerack || handoff) return HANDOFF;
      },
      t(`${PATH}/toast/assigned`, { name: db.name || "" }),
    );
    return { done, handoff, rerack };
  };

  // After the re-rack sheet's Done: the boxes are back, assign again. A box
  // still out opens the sheet again.
  const retryAssign = async (tp, db) => {
    const { rerack } = await assignTp(tp, db, true);
    if (rerack) openRerack(rerack, () => retryAssign(tp, db), db.name || "");
    reload();
  };

  // The DB sheet asks the hand-off question itself (picked card, or a
  // stale list answered TP_PICKED_BY_DB) and passes confirmHandoff.
  const onAssign = async (db, confirmHandoff = false) => {
    const tp = pickerTp;
    const { done, handoff, rerack } = await assignTp(tp, db, confirmHandoff);
    if (handoff) {
      // Stale list: the server says the DB has picked. The sheet's confirm
      // stays open and now asks the hand-off question; the next tap retries.
      setHandoffFrom(handoff.db_name || tp.db?.name || "");
      return;
    }
    // Refused (e.g. already delivered today): close the sheet too, the toast
    // says why.
    setPickerTp(null);
    setHandoffFrom(null);
    if (rerack) {
      openRerack(rerack, () => retryAssign(tp, db), db.name || "");
      return;
    }
    if (done) reload();
  };

  const onRemove = (tp) => {
    setPickerTp(null);
    const handoff = getCardActions(tp).requiresHandoffConfirm;
    setTimeout(
      () =>
        setConfirm({
          type: handoff ? CONFIRM.HANDOFF_UNASSIGN : CONFIRM.UNASSIGN,
          tp,
          handoffDb: tp.db?.name || "",
        }),
      SHEET_SWAP_MS,
    );
  };

  // One Remove; `handoff` / `rerack` as in assignTp.
  const unassignTp = async (tp, confirmHandoff) => {
    let handoff = null;
    let rerack = null;
    const done = await run(
      async () => {
        const response = await unassignDsRoutingTps({
          cx_ids: [tp.cx_id],
          cp_db_id: tp.db?.cp_db_id,
          confirm_handoff: confirmHandoff,
        });
        rerack = getRerackConflict(response);
        handoff = rerack ? null : getHandoffConflict(response);
        if (rerack || handoff) return HANDOFF;
      },
      t(`${PATH}/toast/unassigned`),
    );
    return { done, handoff, rerack };
  };

  const retryUnassign = async (tp) => {
    const { rerack } = await unassignTp(tp, true);
    if (rerack) openRerack(rerack, () => retryUnassign(tp));
    reload();
  };

  const onConfirm = async () => {
    const { type, tp } = confirm;
    const cpDbId = tp.db?.cp_db_id;
    if (type === CONFIRM.UNASSIGN || type === CONFIRM.HANDOFF_UNASSIGN) {
      const { done, handoff, rerack } = await unassignTp(
        tp,
        type === CONFIRM.HANDOFF_UNASSIGN,
      );
      if (handoff) {
        // Stale list: swap to the hand-off wording; the next tap retries.
        setConfirm({
          type: CONFIRM.HANDOFF_UNASSIGN,
          tp,
          handoffDb: handoff.db_name || tp.db?.name || "",
        });
        return;
      }
      setConfirm(null);
      if (rerack) {
        openRerack(rerack, () => retryUnassign(tp));
        return;
      }
      if (done) reload();
      return;
    }
    const done = await run(
      () => markDsRoutingTpOfd({ cx_id: tp.cx_id, cp_db_id: cpDbId }),
      t(`${PATH}/toast/ofd`),
    );
    setConfirm(null);
    if (done) reload();
  };

  const tps = list?.tps || [];
  // Null on a server without filter counts: no pills, `progress` as sent.
  const counts = getRoutingCounts(list, locality);
  const showFilters = Boolean(counts) && !isNext;
  const activeFilter = showFilters ? filter : FILTERS.ALL;
  const rows = useMemo(() => {
    const shown = filterTps(
      filterRoutingTps(list?.tps || [], {
        locality,
        filter: activeFilter,
      }),
      search.query,
    );
    return isNext ? withDateHeaders(shown) : shown;
  }, [list, locality, activeFilter, isNext, search.query]);
  const headerItems = [{ _key: "CHIPS" }, { _key: "PROGRESS" }];
  const bodyRows =
    rows.length > 0 ? rows : isLoading ? [] : [{ _key: "EMPTY" }];
  const emptyKey = search.query
    ? "noMatch"
    : FILTER_PILLS.find((pill) => pill.key === activeFilter)?.empty ||
      "emptyState";

  // Header dropdowns: the locality's bubble is TPs to route (all TPs on
  // Next Orders, or on a server that sends only `count`).
  const localityCount = (item) =>
    isNext || item.to_route == null ? item.count : item.to_route;

  const localityOptions = [
    {
      key: ALL_LOCALITIES,
      label: t(`${PATH}/allAreas`),
      count: counts ? (isNext ? counts.total : counts.to_route) : null,
    },
  ].concat(
    (list.localities || []).map((item) => ({
      key: item.name,
      label: item.name,
      count: localityCount(item),
    })),
  );

  const filterOptions = FILTER_PILLS.map((pill) => ({
    key: pill.key,
    label: t(`${PATH}/${pill.label}`),
    count: counts ? counts[pill.count || "total"] || 0 : null,
  }));

  const selectedLocality =
    localityOptions.find((option) => option.key === locality) ||
    localityOptions[0];
  const selectedFilter =
    filterOptions.find((option) => option.key === activeFilter) ||
    filterOptions[0];

  const renderDropdown = ({ testID, icon, option, active, onPress }) => (
    <Pressable
      testID={testID}
      onPress={onPress}
      style={[styles.dropdown, active && styles.chipActive]}
    >
      {icon}
      <RText
        size={12}
        weight={600}
        color={active ? C.chipRed : C.ink}
        numberOfLines={1}
        style={styles.shrink}
      >
        {option.label}
      </RText>
      {option.count != null ? (
        <View style={[styles.bubble, active && styles.bubbleActive]}>
          <RText size={10} weight={700} color={C.surface}>
            {String(option.count)}
          </RText>
        </View>
      ) : null}
      <View style={styles.grow} />
      <ChevronDown color={active ? C.chipRed : C.muted} />
    </Pressable>
  );

  // Pinned while the cards scroll: locality and filter dropdowns, side by
  // side (the filter only where the server sends its counts).
  const renderHeader = () => (
    <View style={[styles.sticky, styles.dropdowns]}>
      {renderDropdown({
        testID: "ds-routing-locality",
        icon: <Pin />,
        option: selectedLocality,
        active: Boolean(locality),
        onPress: () => setSheet(SHEET.LOCALITY),
      })}
      {showFilters
        ? renderDropdown({
            testID: "ds-routing-filter",
            icon: null,
            option: selectedFilter,
            active: activeFilter !== FILTERS.ALL,
            onPress: () => setSheet(SHEET.FILTER),
          })
        : null}
    </View>
  );

  const renderProgress = () => {
    if (isNext || isAll) {
      return (
        <RText size={12} weight={500} color={C.muted} style={styles.helper}>
          {t(`${PATH}/${isAll ? "allHelper" : "nextHelper"}`)}
        </RText>
      );
    }
    const {
      to_route: toRoute = 0,
      routed = 0,
      total = 0,
    } = counts || list.progress || {};
    const pct = total > 0 ? Math.round((routed / total) * 100) : 0;
    return (
      <View style={styles.progress}>
        <View style={styles.progressTop}>
          <View style={styles.grow}>
            <RText size={9} weight={700} color={C.muted} style={styles.label}>
              {t(`${PATH}/progressLabel`)}
            </RText>
            <RText size={15} weight={700} style={styles.progressValue}>
              <RText size={15} weight={700} color={C.red}>
                {t(`${PATH}/toRoute`, { count: String(toRoute) })}
              </RText>
              <RText size={15} weight={500} color={C.muted}>
                {t(`${PATH}/routedOf`, {
                  routed: String(routed),
                  total: String(total),
                })}
              </RText>
            </RText>
          </View>
          {locality ? (
            <Tag
              label={String(locality).toUpperCase()}
              bg={C.chipRedBg}
              color={C.chipRed}
            />
          ) : null}
        </View>
        <View style={styles.track}>
          <View style={[styles.bar, { width: `${pct}%` }]} />
        </View>
      </View>
    );
  };

  const renderItem = ({ item }) => {
    if (item._key === "CHIPS") return renderHeader();
    if (item._key === "PROGRESS") return renderProgress();
    if (item._key === "EMPTY") {
      return (
        <RText size={14} weight={500} color={C.muted} style={styles.empty}>
          {t(`${PATH}/${emptyKey}`)}
        </RText>
      );
    }
    if (item._type === "DATE_HEADER") {
      const label = eddLabel(item.date, list.date);
      const text = label?.key
        ? t(`${PATH}/edd${label.key === "today" ? "Today" : "Tomorrow"}`)
        : label?.text || item.date;
      return (
        <RText size={9} weight={700} color={C.muted} style={styles.dateHeader}>
          {t(`${PATH}/deliveryOn`, { date: text }).toUpperCase()}
        </RText>
      );
    }
    if (isAll) {
      return (
        <AllOrdersRow item={item} today={list.date} onPick={setPickerTp} />
      );
    }
    return (
      <RoutingTpRow
        item={item}
        today={list.date}
        onAddDb={setPickerTp}
        onChange={setPickerTp}
        onRecommended={(tp) =>
          navigation.navigate("DsRoutingRecommended", { tp })
        }
        onMarkOfd={(tp) => setConfirm({ type: CONFIRM.OFD, tp })}
      />
    );
  };

  const confirmTp = confirm?.tp;

  return (
    <View style={styles.screen}>
      <RoutingHeader
        title={t(`${PATH}/hub/${TITLE_KEY[scope]}`)}
        navigation={navigation}
      />
      <RoutingSearch
        testID="ds-routing-search"
        value={search.text}
        onChangeText={search.setText}
        placeholder={t(`${PATH}/searchPlaceholder`)}
      />
      {isLoading && tps.length === 0 ? (
        <ActivityIndicator size="large" color={C.red} style={styles.loader} />
      ) : (
        <FlatList
          testID="ds-routing-tp-list"
          showsVerticalScrollIndicator={false}
          data={headerItems.concat(bodyRows)}
          keyExtractor={(item) => item._key || getTpKey(item)}
          renderItem={renderItem}
          stickyHeaderIndices={[0]}
          refreshing={isRefreshing}
          onRefresh={() => reload({ refreshing: true })}
          contentContainerStyle={styles.listBody}
        />
      )}

      <OptionSheet
        visible={sheet === SHEET.LOCALITY}
        title={t(`${PATH}/localityTitle`)}
        options={localityOptions}
        selected={locality || ALL_LOCALITIES}
        onSelect={(key) => {
          setLocality(key === ALL_LOCALITIES ? null : key);
          setSheet(null);
        }}
        onClose={() => setSheet(null)}
        testID="ds-routing-locality-sheet"
      />
      <OptionSheet
        visible={sheet === SHEET.FILTER}
        title={t(`${PATH}/filterTitle`)}
        options={filterOptions}
        selected={activeFilter}
        onSelect={(key) => {
          setFilter(key);
          setSheet(null);
        }}
        onClose={() => setSheet(null)}
        testID="ds-routing-filter-sheet"
      />
      <DbPickerSheet
        tp={pickerTp}
        submitting={submitting}
        onClose={() => {
          setPickerTp(null);
          setHandoffFrom(null);
        }}
        onAssign={onAssign}
        handoffFrom={handoffFrom}
        onRemove={onRemove}
      />
      <ConfirmPopup
        visible={Boolean(confirm)}
        title={t(CONFIRM_COPY[confirm?.type]?.title || CONFIRM_COPY.OFD.title, {
          name: confirmTp ? tpName(confirmTp) : "",
          db: confirm?.handoffDb || confirmTp?.db?.name || "",
        })}
        confirmLabel={t(
          CONFIRM_COPY[confirm?.type]?.label || CONFIRM_COPY.OFD.label,
        )}
        cancelLabel={t(`${PATH}/cancel`)}
        confirmColor={confirm?.type === CONFIRM.OFD ? C.green : C.red}
        loading={submitting}
        onCancel={() => setConfirm(null)}
        onConfirm={onConfirm}
      />
      {rerackSheet}
    </View>
  );
};

export default DsRoutingTpList;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.page },
  loader: { flex: 1 },
  listBody: { paddingBottom: 24 },
  // Opaque so the cards don't show through the pinned header.
  sticky: { backgroundColor: C.page, paddingTop: 12 },
  dropdowns: {
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  dropdown: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 13,
  },
  chipActive: { backgroundColor: C.chipRedBg, borderColor: C.chipRed },
  bubble: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    paddingHorizontal: 5,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: C.muted,
  },
  bubbleActive: { backgroundColor: C.chipRed },
  progress: {
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 14,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  progressTop: { flexDirection: "row", alignItems: "center", gap: 10 },
  grow: { flex: 1 },
  shrink: { flexShrink: 1 },
  label: { letterSpacing: 0.6 },
  progressValue: { paddingTop: 2 },
  track: {
    height: 7,
    backgroundColor: C.track,
    borderRadius: 5,
    overflow: "hidden",
    marginTop: 10,
  },
  bar: { height: "100%", backgroundColor: C.green, borderRadius: 5 },
  helper: { paddingHorizontal: 16, paddingBottom: 12, lineHeight: 18 },
  dateHeader: {
    letterSpacing: 0.6,
    paddingHorizontal: 16,
    paddingBottom: 8,
    paddingTop: 4,
  },
  empty: { textAlign: "center", marginTop: 60 },
});
